import { useState, useEffect, useMemo, useRef } from "react";
import { css, styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { SidePanelContainer } from "../ui";
import { Divider } from "../../primitives";
import type { CreateWorkflowInput, UpdateWorkflowInput, FilterQueryInput } from "@acto/gql/graphql";
import type { Workflow } from "@acto/taskpane/core/domain";
import { TriggerRulesSection, ConfigurationSection } from "./ui";
import type { WorkflowConfigurationValues } from "./ui/ConfigurationSection";
import { clsx } from "clsx";

interface CreateWorkflowSidePanelProps {
  onSave: (input: CreateWorkflowInput) => void;
  onUpdate?: (id: string, input: UpdateWorkflowInput) => void;
  editingWorkflow?: Workflow | null;
  filterQuery: FilterQueryInput;
  onFilterQueryChange: (query: FilterQueryInput) => void;
  onValidationChange?: (isValid: boolean) => void;
  onFormChange?: () => void;
  disabled?: boolean;
}

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[6],
  paddingLeft: theme.managementView.spacing[6],
}));

const TitleGroup = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[2],
}));

const TitleInput = styled("input")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[21],
  fontWeight: theme.managementView.font.weight[500],
  lineHeight: "30px",
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  backgroundColor: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  outline: "none",

  "&:focus": {
    color: theme.managementView.colors.neutral[100],
  },

  "&::placeholder": {
    color: theme.managementView.colors.neutral[56],
  },
}));

const DescriptionInput = styled("input")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[500],
  lineHeight: "30px",
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  backgroundColor: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  outline: "none",
  width: "100%",

  "&:focus": {
    color: theme.managementView.colors.neutral[100],
  },

  "&::placeholder": {
    color: theme.managementView.colors.neutral[56],
  },
}));

const filledInputStyles = css(({ theme }) => ({
  color: theme.managementView.colors.neutral[100],
}));

const SectionHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[8],
  paddingBottom: theme.managementView.spacing[8],
}));

const SectionTitle = styled("h3")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[17],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[100],
  margin: 0,
}));

const SectionDescription = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  lineHeight: "20px",
  color: theme.managementView.colors.neutral[56],
  margin: 0,
}));

const Form = styled("form")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const disabledFormStyles = css({
  opacity: 0.5,
  pointerEvents: "none",
});

export const CreateWorkflowSidePanel = ({
  onSave,
  onUpdate,
  editingWorkflow,
  filterQuery,
  onFilterQueryChange,
  onValidationChange,
  onFormChange,
  disabled,
}: CreateWorkflowSidePanelProps) => {
  const t = useTranslations("managementView.workflow");
  const isEditing = !!editingWorkflow;

  const [name, setName] = useState(editingWorkflow?.name ?? "");
  const [description, setDescription] = useState(editingWorkflow?.description ?? "");
  const [configuration, setConfiguration] = useState<WorkflowConfigurationValues>({
    repeat: editingWorkflow?.repeat ?? true,
    dueDate: editingWorkflow?.dueDate ?? "1m",
    pauseTill: editingWorkflow?.pauseTill ?? "1m",
    fixedResponsibleEmployeeId: editingWorkflow?.fixedResponsibleEmployee?.id,
  });

  const allConditionsValid = useMemo(() => {
    const conditions = filterQuery.groups.flatMap((g) => g.conditions);
    if (conditions.length === 0) return false;

    return conditions.every((condition) =>
      condition.params.every((param) => {
        if (param.name === "timeframe") return true;
        if (param.stringValue != null) return param.stringValue.length > 0;
        if (param.stringValues != null) return param.stringValues.length > 0;
        if (param.dateValue != null)
          return typeof param.dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(param.dateValue);
        if (param.numericValue != null) return true;
        if (param.intValue != null) return true;
        if (param.boolValue != null) return true;
        return false;
      }),
    );
  }, [filterQuery]);

  useEffect(() => {
    onValidationChange?.(name.trim().length > 0 && allConditionsValid);
  }, [name, allConditionsValid, onValidationChange]);

  const onFormChangeRef = useRef(onFormChange);
  useEffect(() => {
    onFormChangeRef.current = onFormChange;
  });

  useEffect(() => {
    onFormChangeRef.current?.();
  }, [name, description, configuration]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing && onUpdate) {
      onUpdate(editingWorkflow.id, {
        name: name || undefined,
        description,
        query: filterQuery,
        repeat: configuration.repeat,
        dueDate: configuration.dueDate,
        pauseTill: configuration.repeat ? configuration.pauseTill : undefined,
        fixedResponsibleEmployeeId: configuration.fixedResponsibleEmployeeId,
      });
    } else {
      onSave({
        name: name || t("createWorkflow.titlePlaceholder"),
        description,
        query: filterQuery,
        repeat: configuration.repeat,
        dueDate: configuration.dueDate,
        pauseTill: configuration.repeat ? configuration.pauseTill : undefined,
        fixedResponsibleEmployeeId: configuration.fixedResponsibleEmployeeId,
      });
    }
  };

  return (
    <Form id="create-workflow-form" onSubmit={handleSubmit}>
      <SidePanelContainer className={clsx(disabled && disabledFormStyles)}>
        <Header>
          <TitleGroup>
            <TitleInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("createWorkflow.titlePlaceholder")}
              className={clsx(name.length > 0 && filledInputStyles)}
            />
            <DescriptionInput
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("createWorkflow.descriptionPlaceholder")}
              className={clsx(description.length > 0 && filledInputStyles)}
            />
          </TitleGroup>
        </Header>

        <Divider />

        <SectionHeader>
          <SectionTitle>{t("createWorkflow.triggerTitle")}</SectionTitle>
          <SectionDescription>{t("createWorkflow.triggerDescription")}</SectionDescription>
        </SectionHeader>

        <TriggerRulesSection filterQuery={filterQuery} onFilterQueryChange={onFilterQueryChange} />

        <Divider />

        <SectionTitle>{t("createWorkflow.configurationTitle")}</SectionTitle>

        <ConfigurationSection
          configuration={configuration}
          onConfigurationChange={setConfiguration}
          editingEmployee={editingWorkflow?.fixedResponsibleEmployee ?? undefined}
        />
      </SidePanelContainer>
    </Form>
  );
};
